/**
 * Display-only: which OCR words of a field's cited lines spell its value.
 *
 * A citation names whole OCR lines, so "Purchase order: WCS-PO-9100" outlined
 * the label too. The engine records the value's own words (evidenceWordIndexes,
 * FieldWordCitation.cs); documents stored before that, or a field whose value
 * changed since, are narrowed here with the same rule. Nothing found means the
 * whole cited lines, exactly as before. Never a citation, truth or a value.
 */
export type CitationWord = { text: string; lineIndex: number };

/** Where a word's ink is on the page: the worker's measured box when it sent one, else the line's even split. */
export const inkBox = <B>(word: { box: B; measuredBox?: B | null }): B =>
  word.measuredBox ?? word.box;
export type EvidenceCitation = {
  lineIndexes: readonly number[];
  value?: string | null;
  stored?: readonly number[] | null;
};

// Case, punctuation and spacing never decide a match: "W" + "CS-PO-9100" spells WCS-PO-9100.
const key = (text: string) =>
  text
    .normalize('NFKC')
    .toLocaleLowerCase()
    .replace(/[^\p{L}\p{N}]+/gu, '');
const TOKEN_SHARE = 0.6;
const TOKEN_WINDOW = 12;

/** Word indexes (into the page's words) that spell `value` among `candidates`, or null. */
function spell(
  words: readonly CitationWord[],
  candidates: readonly number[],
  value: string,
): number[] | null {
  const wanted = key(value);
  if (!wanted || !candidates.length) return null;
  // One entry per kept character: the candidate it came from, and whether a word edge
  // or printed punctuation sits before / after it.
  let text = '';
  const owners: number[] = [],
    opens: boolean[] = [],
    closes: boolean[] = [];
  candidates.forEach((index, position) => {
    const raw = [...(words[index]?.text ?? '').normalize('NFKC').toLocaleLowerCase()];
    raw.forEach((char, at) => {
      const kept = key(char);
      if (!kept) return;
      for (let unit = 0; unit < kept.length; unit++) {
        owners.push(position);
        opens.push(unit === 0 && (at === 0 || !key(raw[at - 1])));
        closes.push(unit === kept.length - 1 && (at === raw.length - 1 || !key(raw[at + 1])));
      }
      text += kept;
    });
  });
  const keys = candidates.map((index) => key(words[index]?.text ?? ''));
  const chosen = new Set<number>();
  for (let at = text.indexOf(wanted); at >= 0; at = text.indexOf(wanted, at + 1)) {
    const end = at + wanted.length - 1;
    // Starts and ends where print does: "PO:WCS-PO-9100" holds the value after its colon,
    // but "100" is not in "1000" and "no" is not in "Number".
    if (!opens[at] || !closes[end]) continue;
    for (let position = owners[at]; position <= owners[end]; position++)
      chosen.add(candidates[position]);
    at = end;
  }
  if (chosen.size) return [...chosen].sort((a, b) => a - b);
  // Printed in another order or form ("07/22/2026" for 2026-07-22): the smallest run of
  // words that holds every part of the value and little else.
  const tokens = value
    .normalize('NFKC')
    .toLocaleLowerCase()
    .split(/[^\p{L}\p{N}]+/u)
    .filter(Boolean);
  if (tokens.length < 2) return null;
  const total = tokens.reduce((sum, token) => sum + token.length, 0);
  let best: { from: number; to: number; length: number } | null = null;
  for (let from = 0; from < keys.length; from++) {
    if (!keys[from] || !tokens.some((token) => keys[from].includes(token))) continue;
    for (let to = from; to < Math.min(keys.length, from + TOKEN_WINDOW); to++) {
      const joined = keys.slice(from, to + 1).join('');
      if (!tokens.every((token) => joined.includes(token))) continue;
      if (total / joined.length >= TOKEN_SHARE && (!best || joined.length < best.length))
        best = { from, to, length: joined.length };
      break;
    }
  }
  return best ? candidates.slice(best.from, best.to + 1) : null;
}

/**
 * The words of `lineIndexes` that make up `value`, every occurrence; null when none
 * do (show the whole lines). A stored word citation is used only while it still
 * lies on the cited lines and still spells the value.
 */
export function valueWordIndexes(
  words: readonly CitationWord[],
  lineIndexes: readonly number[],
  value: string,
  stored?: readonly number[] | null,
): number[] | null {
  const lines = new Set(lineIndexes);
  if (!lines.size || !key(value)) return null;
  if (
    stored?.length &&
    new Set(stored).size === stored.length &&
    stored.every(
      (index) =>
        Number.isInteger(index) &&
        index >= 0 &&
        index < words.length &&
        lines.has(words[index].lineIndex),
    )
  ) {
    const sorted = [...stored].sort((a, b) => a - b);
    if (spell(words, sorted, value)) return sorted;
  }
  return spell(
    words,
    words.flatMap((word, index) => (lines.has(word.lineIndex) ? [index] : [])),
    value,
  );
}

/** Every word to outline for these citations; one without a word match keeps its whole lines. */
export function citedWordIndexes(
  words: readonly CitationWord[],
  citations: readonly EvidenceCitation[],
): number[] {
  const outlined = new Set<number>();
  for (const citation of citations) {
    const narrowed = citation.value
      ? valueWordIndexes(words, citation.lineIndexes, citation.value, citation.stored)
      : null;
    if (narrowed) for (const index of narrowed) outlined.add(index);
    else {
      const lines = new Set(citation.lineIndexes);
      words.forEach((word, index) => {
        if (lines.has(word.lineIndex)) outlined.add(index);
      });
    }
  }
  return [...outlined].sort((a, b) => a - b);
}
